import { computed, Injectable, Signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Amplify } from 'aws-amplify';
import * as Auth from 'aws-amplify/auth';
import {
  CognitoIdentityProviderClient,
  GlobalSignOutCommand,
} from '@aws-sdk/client-cognito-identity-provider';

import { environment } from '@env';
import { persistentSignal } from 'app/signals/persistent-signal';
import {
  Roles,
  StepperStatus,
} from 'gql/graphql';
import { URLS } from '@constants/urls';

export type FirstAccessWizardStep = Pick<StepperStatus, 'step'> & {
  stepDetails: Pick<
    StepperStatus['stepDetails'],
    'termsAndConditions' | 'gateway'
  > & {
    devices?: Omit<
      StepperStatus['stepDetails']['devices'],
      'groups' | 'consumption'
    > | null;
    property?: Omit<
      StepperStatus['stepDetails']['property'],
      | 'status'
      | 'todaysConsumption'
      | 'thisMonthsConsumption'
      | 'actualCosts'
      | 'consumption'
    > | null;
  };
};
export interface IUser {
  name: string;
  lastName: string;
  imgUrl: string;
  company: string;
  registrationCompany?: string;
  country?: string;
  city?: string;
  postalCode?: string;
  address?: string;
  fullAddress?: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}
export interface ILogin {
  email: string;
  password: string;
  /* showPassword: boolean;
  code: string;
  name: string; */
}

export interface UserWithID extends Auth.FetchUserAttributesOutput {
  id: string;
  imgUrl?: string;
  role?: Roles;
  licenseId?: string;
  permissions?: string;
}

const ACCESS_TOKEN = 'access-token';
const USER_KEY = 'auth-user';
const APP_LANGUAGE = 'app-language';
const FIRST_ACCESS_WIZARD_STEP = 'first-access-wizard-step';
const WIZARD_FROM_PROPERTY_LIST = 'wizard-from-property-list';

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  loggedInUser = persistentSignal<UserWithID | null>(null, {
    key: USER_KEY,
  });
  private isFirstLogin = persistentSignal<boolean>(false, {
  key: 'IS_FIRST_LOGIN',
  });

  private accessToken = persistentSignal<Auth.JWT | undefined | null>(null, {
    key: ACCESS_TOKEN,
    serializeFn(original) {
      const serialized = original?.toString() ?? '';
      console.log('Serializing token, length:', serialized.length);
      return serialized;
    },
    parseFn(serialized) {
      console.log(
        'Parsing token from storage:',
        !!serialized,
        'length:',
        serialized?.length
      );
      try {
        if (!serialized) {
          console.log('No token to parse');
          return null;
        }
        const decoded = Auth.decodeJWT(serialized);
        console.log('Token parsed successfully, exp:', decoded?.payload?.exp);
        return decoded;
      } catch (error) {
        console.error('Error parsing token from storage:', error);
        return null;
      }
    },
  });
  private appLanguage = persistentSignal<string>('', {
    key: APP_LANGUAGE,
  });
  private firstAccessWizardStep = persistentSignal<FirstAccessWizardStep>(
    { step: 0, stepDetails: { termsAndConditions: false } },
    {
      key: FIRST_ACCESS_WIZARD_STEP,
    }
  );

  private authenticationSubject: BehaviorSubject<any>;

  token: Signal<Auth.JWT | undefined | null>;
  loggedUser: Signal<UserWithID | null>;
  isFirstLoginValue: Signal<boolean>;
  language: Signal<string>;
  wizardStep: Signal<FirstAccessWizardStep>;

  constructor() {
    this.token = this.accessToken.asReadonly();
    this.loggedUser = this.loggedInUser.asReadonly();
    this.language = this.appLanguage.asReadonly();
    this.wizardStep = this.firstAccessWizardStep.asReadonly();
    this.isFirstLoginValue = this.isFirstLogin.asReadonly();
    Amplify.configure({
      Auth: {
        Cognito: {
          ...environment.cognito,
          userPoolClientId: environment.cognito.userPoolWebClientId,
        },
      },
    });

    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  public signUp(user: IUser): Promise<any> {
    const dataToSend: Auth.SignUpInput = {
      username: user.email,
      password: user.password,
      options: {
        userAttributes: {
          email: user.email,
          phone_number: user.phone,
          given_name: user.name,
          family_name: user.lastName,
          address: JSON.stringify({
            postalCode: user.postalCode,
            country: user.country,
            locality: user.city,
            street_address: user.address,
          }),
          'custom:acceptTerms': `${user.acceptTerms}`,
        },
        // Note: it's not really automatic, as it requires an OTP code to login anyways
        // autoSignIn: true,
      },
    };
    if (user.registrationCompany) {
      dataToSend.options!.userAttributes = {
        ...dataToSend.options!.userAttributes,
        'custom:registrationCompany': user.registrationCompany,
      };
    } else {
      dataToSend.options!.userAttributes = {
        ...dataToSend.options!.userAttributes,
        'custom:company': user.company,
      };
    }
    return Auth.signUp({
      ...dataToSend,
    });
  }

  public async checkUserExists(email: string): Promise<boolean> {
    try {
      // Strategia: tenta il sign-in con una password sbagliata
      // Se l'utente esiste, otterremo "NotAuthorizedException"
      // Se l'utente non esiste, otterremo "UserNotFoundException"
      await Auth.signIn({
        username: email,
        password: 'fake-password-for-user-existence-check-12345',
      });

      // Se arriviamo qui senza errore (molto improbabile), l'utente esiste
      console.log('Sign-in succeeded unexpectedly, user exists');
      return true;
    } catch (error: any) {
      console.log('CheckUserExists - Full error object:', error);
      console.log('Error name:', error.name);
      console.log('Error message:', error.message);
      console.log('Error code:', error.code);

      if (
        error.name === 'NotAuthorizedException' ||
        error.message?.includes('Incorrect username or password') ||
        error.message?.includes('NotAuthorizedException')
      ) {
        console.log('User exists - NotAuthorizedException received');
        return true;
      }

      // Se l'errore è "UserNotFoundException", l'utente non esiste
      if (
        error.name === 'UserNotFoundException' ||
        error.message?.includes('UserNotFoundException') ||
        error.message?.includes('User does not exist')
      ) {
        console.log('User does not exist - UserNotFoundException received');
        return false;
      }

      // Se l'utente non è confermato, esiste ma non ha completato la verifica
      if (
        error.name === 'UserNotConfirmedException' ||
        error.message?.includes('UserNotConfirmedException')
      ) {
        console.log('User exists but not confirmed');
        return true;
      }

      // Se c'è già un utente autenticato, non possiamo verificare l'esistenza di altri utenti
      if (
        error.name === 'UserAlreadyAuthenticatedException' ||
        error.message?.includes('UserAlreadyAuthenticatedException') ||
        error.message?.includes('There is already a signed in user')
      ) {
        console.log(
          'Another user is already authenticated, cannot check user existence'
        );
        return true;
      }

      if (
        error.name === 'NetworkError' ||
        error.message?.includes('Network') ||
        error.message?.includes('timeout') ||
        error.message?.includes('fetch')
      ) {
        console.log('Network error occurred, assuming user does not exist');
        return false;
      }

      console.warn(
        'Unexpected error checking user existence, assuming user does NOT exist:',
        error
      );
      return false;
    }
  }

  public confirmSignUp(user: IUser, confirmationCode: string): Promise<any> {
    return Auth.confirmSignUp({
      username: user.email,
      confirmationCode,
    });
  }

  public async signIn(user: ILogin): Promise<URL | undefined> {
    return Auth.signIn({
      username: user.email,
      password: user.password,
    }).then(async(res) => this.handleSignInNextSteps(res));
  }

  public isTokenExpiring() {
    const tokenExpTime = this.token()?.payload?.exp;
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    if (this.token() && tokenExpTime) {
      // Check if the token will expire in the next 5 minutes (300 seconds)
      const timeLeft = tokenExpTime - currentTime;
      return timeLeft < 300;
    }
    return true;
  }

  public async checkAuthenticationStatus(): Promise<boolean> {
    try {
      console.log('Checking authentication status...');
      console.log('Current user:', !!this.loggedUser());
      console.log('Current token exists:', !!this.token());
      console.log('Token expiring:', this.isTokenExpiring());

      // Prima prova: controlla se abbiamo già tutto quello che serve
      if (this.loggedUser() && this.token() && !this.isTokenExpiring()) {
        console.log('User and valid token already available');
        return true;
      }

      // Seconda prova: prova a ottenere una sessione fresca da Cognito
      console.log('Attempting to fetch fresh session from Cognito...');
      try {
        const session = await Auth.fetchAuthSession();
        console.log('Session fetched:', !!session);
        console.log('Access token in session:', !!session.tokens?.accessToken);

        if (session.tokens?.accessToken) {
          // Aggiorna il token nel servizio
          this.accessToken.set(session.tokens.accessToken);
          console.log('Token updated in service');

          // Recupera le informazioni utente se non le abbiamo
          if (!this.loggedUser()) {
            console.log('Fetching user info...');
            try {
              await this.setUserInfo();
              console.log('User info set successfully');
            } catch (userError) {
              console.error('Failed to get user info:', userError);
              return false;
            }
          }

          this.authenticationSubject.next(true);
          console.log('Authentication successful');
          return true;
        } else {
          console.log('No access token in session');
          return false;
        }
      } catch (sessionError) {
        console.error('Failed to fetch session:', sessionError);
        return false;
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  public cleanSession() {
    this.loggedInUser.set(null);
    this.accessToken.set(null);
    this.authenticationSubject.next(false);
  }

  public async refreshAccessToken(): Promise<void> {
    try {
      console.log('Refreshing access token...');
      const { tokens } = await Auth.fetchAuthSession({ forceRefresh: true });
      if (tokens && tokens.accessToken) {
        console.log('New token received, updating signal...');
        this.accessToken.set(tokens.accessToken);
        console.log('Token signal updated successfully');
      } else {
        console.error('Access Token Expired: ', tokens);
        this.cleanSession();
      }
    } catch (error) {
      console.error('Refresh Token Expired: ', error);
      this.cleanSession();
    }
  }

  public async getToken(): Promise<string | undefined> {
    try {
      // Controlla se il token è scaduto o sta per scadere
      if (this.isTokenExpiring()) {
        console.log('Token is expiring, refreshing...');
        await this.refreshAccessToken();
      }

      // Double-check: se ancora non abbiamo un token valido, forza il refresh
      const currentToken = this.token()?.toString();
      if (!currentToken) {
        console.warn('No token available, forcing session refresh');
        const session = await Auth.fetchAuthSession({ forceRefresh: true });
        this.accessToken.set(session.tokens?.accessToken);
        return session.tokens?.accessToken?.toString();
      }

      return currentToken;
    } catch (error) {
      console.error('Error getting token:', error);
      // Fallback: prova a ottenere una sessione fresca
      try {
        console.log('Attempting fallback token retrieval...');
        const session = await Auth.fetchAuthSession({ forceRefresh: true });
        this.accessToken.set(session.tokens?.accessToken);
        return session.tokens?.accessToken?.toString();
      } catch (fallbackError) {
        console.error('Fallback token retrieval failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  // TODO evaluate adding return type
  public async handleSignInNextSteps(output: Auth.SignInOutput) {
    const { nextStep } = output;
    this.isFirstLogin.set(false);
    switch (nextStep.signInStep) {
      // This happens if Cognito is not configured properly,
      // i.e. MFA is not set as Optional
      // case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':'DONE'

      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        const totpSetupDetails = nextStep.totpSetupDetails;
        const appName = 'ABB Ability Insite Energy Pro';
        const setupUri = totpSetupDetails.getSetupUri(appName);
        // Open setupUri with an authenticator APP to retrieve an OTP code
        return setupUri;

      // This happens when the user resets the MFA configuration
      // and gets the code via email
      // The UI changes text to reflect the new code source
      // (email instead of authenticator)
      // case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      // break;

      // When a user logs in normally, i.e. having already
      // completed the MFA setup via QR code
      // Also, when clicking on reset MFA?
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        // display user to enter otp code;
        break;

      // In theory, only after a newly registered user confirms his/her
      // email address and tries to login with user and password
      case 'DONE':
        this.setUserInfo();
        this.authenticationSubject.next(true);
        await this.refreshAccessToken();
        this.isFirstLogin.set(true);
        return this.setUpTOTP();

      default:
        break;
    }
    return;
  }

  public async confirmSignIn(totpCode: string): Promise<any> {
    return Auth.confirmSignIn({ challengeResponse: totpCode }).then(
      async () => {
        this.setUserInfo();
        this.authenticationSubject.next(true);
        await this.refreshAccessToken();
      }
    );
  }

  public async setUpTOTP(): Promise<URL> {
    return Auth.setUpTOTP().then((totpSetupDetails) => {
      const appName = 'ABB Ability Insite Energy Pro';
      const setupUri = totpSetupDetails.getSetupUri(appName);
      // Open setupUri with an authenticator APP to retrieve an OTP code
      return setupUri;
    });
  }

  public async verifyTOTPSetup(code: string): Promise<void> {
    await Auth.verifyTOTPSetup({
      code,
    });
    await Auth.updateMFAPreference({
        totp: 'PREFERRED',
        email: 'NOT_PREFERRED',
        sms: 'DISABLED',
      });
    }

  public setUserFirstAccessStep(step: FirstAccessWizardStep) {
    this.firstAccessWizardStep.set(step);
  }

  public async setUserInfo(): Promise<void> {
    try {
      const user = await this.getUser();
      const attributes = await this.getUserInfo();

      this.loggedInUser.set({
        ...attributes,
        id: `u#${user.userId}`,
      });
    } catch (error) {
      console.error('Error setting user info:', error);
      throw error;
    }
  }

  public async signOut(): Promise<any> {
    return Auth.signOut({ global: true }).then(() => {
      this.cleanSession();
    });
  }

  isLoggedIn = computed(() => this.loggedUser() !== null);
  public isAuthenticated(): boolean {
    // Controllo rapido per chiamate sincrone
    if (this.authenticationSubject.value) {
      return true;
    }

    // Controllo se abbiamo sia utente che token valido
    const hasUser = this.isLoggedIn();
    const hasValidToken = this.token() && !this.isTokenExpiring();

    return hasUser && !!hasValidToken;
  }

  public getAuthenticationSubject(): BehaviorSubject<boolean> {
    return this.authenticationSubject;
  }

  public getUserInfo(): Promise<Auth.FetchUserAttributesOutput> {
    return Auth.fetchUserAttributes();
  }

  public getUser(): Promise<Auth.GetCurrentUserOutput> {
    return Auth.getCurrentUser();
  }

  public refreshUserInfo() {
    this.getUserInfo().then((attributes: Auth.FetchUserAttributesOutput) => {
      this.loggedInUser.set({
        id: this.loggedInUser()?.id!,
        imgUrl: this.loggedInUser()?.imgUrl,
        licenseId: this.loggedInUser()?.licenseId,
        role: this.loggedInUser()?.role,
        permissions: this.loggedInUser()?.permissions,
        ...attributes,
      });
    });
  }

  public async confirmUserAttribute(
    userAttributeKey: Auth.VerifiableUserAttributeKey,
    confirmationCode: string
  ): Promise<any> {
    return Auth.confirmUserAttribute({
      userAttributeKey,
      confirmationCode,
    }).finally(() => this.refreshUserInfo());
  }

  public handleUpdateUserAttributeNextSteps(
    output: Auth.UpdateUserAttributeOutput
  ): {
    attribute: Auth.UserAttributeKey | undefined;
    delivery: string | undefined;
  } | void {
    const { nextStep } = output;
    const { updateAttributeStep, codeDeliveryDetails } = nextStep;

    switch (updateAttributeStep) {
      case 'CONFIRM_ATTRIBUTE_WITH_CODE':
        /* const attributeName = codeDeliveryDetails?.attributeName; // Get the attribute name dynamically
        console.log(
          `${attributeName}: Confirmation code was sent to ${codeDeliveryDetails?.deliveryMedium}.`
        ); */
        // Collect the confirmation code from the user and pass it to confirmUserAttribute for ${attributeName}.
        return {
          attribute: codeDeliveryDetails?.attributeName,
          delivery: codeDeliveryDetails?.deliveryMedium,
        };
      case 'DONE':
        console.log(`Attribute successfully updated.`);
        break;
      default:
        console.log(`Unknown updateAttributeStep: ${updateAttributeStep}`);
        break;
    }
  }

  public async updateUser(userAttributes: Partial<IUser>): Promise<any> {
    let userAttributesToSend: Auth.UpdateUserAttributesInput['userAttributes'] =
      {};

    // Il tuo mapping esistente (non cambia)
    Object.keys(userAttributes).map(async (k) => {
      switch (k) {
        case 'name':
          userAttributesToSend = {
            ...userAttributesToSend,
            given_name: userAttributes.name,
          };
          break;
        case 'lastName':
          userAttributesToSend = {
            ...userAttributesToSend,
            family_name: userAttributes.lastName,
          };
          break;
        case 'phone':
          userAttributesToSend = {
            ...userAttributesToSend,
            phone_number: userAttributes.phone,
          };
          break;
        case 'postalCode':
          userAttributesToSend = {
            ...userAttributesToSend,
            address: JSON.stringify({
              ...JSON.parse(userAttributesToSend.address ?? '{}'),
              postalCode: userAttributes.postalCode,
            }),
          };
          break;
        case 'country':
          userAttributesToSend = {
            ...userAttributesToSend,
            address: JSON.stringify({
              ...JSON.parse(userAttributesToSend.address ?? '{}'),
              country: userAttributes.country,
            }),
          };
          break;
        case 'city':
          userAttributesToSend = {
            ...userAttributesToSend,
            address: JSON.stringify({
              ...JSON.parse(userAttributesToSend.address ?? '{}'),
              locality: userAttributes.city,
            }),
          };
          break;
        case 'address':
          userAttributesToSend = {
            ...userAttributesToSend,
            address: JSON.stringify({
              ...JSON.parse(userAttributesToSend.address ?? '{}'),
              street_address: userAttributes.address,
            }),
          };
          break;
        case 'email':
          userAttributesToSend = {
            ...userAttributesToSend,
            email: userAttributes.email,
          };
          break;
        case 'acceptTerms':
          userAttributesToSend = {
            ...userAttributesToSend,
            ['custom:acceptTerms']: userAttributes.acceptTerms?.toString(),
          };
          break;
        case 'company':
          userAttributesToSend = {
            ...userAttributesToSend,
            ['custom:company']: userAttributes.company,
          };
          break;
      }
    });

    try {
      console.log('Updating user attributes...');
      const result = await Auth.updateUserAttributes({
        userAttributes: {
          ...userAttributesToSend,
        },
      });

      // IMPORTANTE: Forza immediatamente il refresh del token
      // try {
      //   console.log('Forcing token refresh after updateUserAttributes...');
      //   const newSession = await Auth.fetchAuthSession({ forceRefresh: true });
      //   await Auth.getCurrentUser();

      //   // Aggiorna il token nel servizio
      //   this.accessToken.set(newSession.tokens?.accessToken);
      //   console.log('Token successfully refreshed and updated in service');
      // } catch (refreshError) {
      //   console.error(
      //     'Critical: Token refresh failed after updateUserAttributes:',
      //     refreshError
      //   );
      //   throw refreshError;
      // }

      // Processa il risultato come prima
      if (result && Object.keys(result)?.length > 0) {
        return Object.values(result).map((k) =>
          this.handleUpdateUserAttributeNextSteps(k)
        );
      }
      return result;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    } finally {
      await this.refreshUserInfo();
    }
  }

  public async changePassword(input: Auth.UpdatePasswordInput) {
    try {
      await Auth.updatePassword({
        oldPassword: input.oldPassword,
        newPassword: input.newPassword,
      });
      await this.performGlobalSignOut();
      await Auth.signOut();
      this.cleanSession();

      return true;
    } catch (error) {
      console.error('Password change failed at some step:', error);
      throw error;
    }
  }

  private async performGlobalSignOut() {
    try {
      const session = await Auth.fetchAuthSession();
      const accessToken = session.tokens?.accessToken.toString();
      if (!accessToken) {
        throw new Error('Access token not found.');
      }

      const client = new CognitoIdentityProviderClient({
        region: environment.maps.region,
      });

      const command = new GlobalSignOutCommand({
        AccessToken: accessToken,
      });
      await client.send(command);
    } catch (error) {
      console.error('Global sign out failed', error);
      throw error;
    }
  }

  public async resetPassword(username: string): Promise<{
    attribute: Auth.UserAttributeKey | undefined;
    delivery: string | undefined;
    destination: string | undefined;
  } | void> {
    try {
      return Auth.resetPassword({ username }).then((output) => {
        if (output) {
          return this.handleResetPasswordNextSteps(output);
        }
        return;
      });
    } catch (error) {
      console.error(error);
    }
  }

  private handleResetPasswordNextSteps(output: Auth.ResetPasswordOutput): {
    attribute: Auth.UserAttributeKey | undefined;
    delivery: 'EMAIL' | 'SMS' | 'PHONE' | 'UNKNOWN' | undefined;
    destination: string | undefined;
  } | void {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        /* console.log(
          `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`
        ); */
        // Collect the confirmation code from the user and pass to confirmResetPassword.
        return {
          attribute: codeDeliveryDetails?.attributeName,
          delivery: codeDeliveryDetails?.deliveryMedium,
          destination: codeDeliveryDetails?.destination,
        };
      case 'DONE':
        console.log('Successfully reset password.');
        break;
    }
  }

  public async confirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  }: Auth.ConfirmResetPasswordInput): Promise<void> {
    try {
      await Auth.confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
      await this.performGlobalSignOut();
      await Auth.signOut();
      this.cleanSession();
      return;
    } catch (error) {
      console.log(error);
    }
  }

  public setAppLanguage(appLang: string) {
    this.appLanguage.set(appLang);
  }

  public updateLoggedInUserImgUrl(imgUrl: string) {
    this.loggedInUser.update((prev) => (prev ? { ...prev, imgUrl } : null));
  }

  public updateLoggedInUserLicenseId(licenseId: string) {
    this.loggedInUser.update((prev) => (prev ? { ...prev, licenseId } : null));
  }

  public updateLoggedInUserRole(role: Roles) {
    this.loggedInUser.update((prev) => (prev ? { ...prev, role } : null));
  }

  public updateLoggedInUserPermissions(
    permissions: Record<
      keyof URLS,
      { viewer: boolean; editing: boolean }
    > | null
  ) {
    this.loggedInUser.update((prev) =>
      prev ? { ...prev, permissions: JSON.stringify(permissions) } : null
    );
  }
}


